import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeModal, showPageLoading, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import defaultThumbnailBase64 from 'assets/images/thumbnail-default.png';

import type { Config, Exercises } from '../type';

import { checkRole, getExtFileName, hasEmptyValues, randomString, trimObject } from 'helpers';
import { base64ToFile } from 'helpers/convertBase64ToFile';
import { UploadFile } from 'helpers/upload-file';
import { useString } from 'hooks';
import { apiCall, closeLoading } from 'hooks/service';
import { isEmpty, isEqual } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { useWidthAndHeightFile } from '../../../../hooks/widthAndHeightFile';
import { defaultExcercises } from '../constant';
import { ConfigType, ExerciseContentCategory, LevelsExercise as levelsExercise } from '../enum';
import { exercisesSchema } from '../schema';

export const useExercisesFormController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { id, institutionId } = params;

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const isExcercisesAdd = location.pathname.includes(`${PATH.ADD_EXERCISES}`);

  const userProfile = useAuthStore(userProfileSelector);

  const [configs, setConfigs] = useState<Config[]>([]);

  const [levels, setLevels] = useState<number[]>([]);

  const [exerciseDetail, setExerciseDetail] = useState<Exercises>();

  const [video, setVideo] = useState<File | null>(null);

  const [thumbnail, setThumbnail] = useState<File | null>(null);

  const [category, setCategory] = useState<string>();

  const videoUrl = useString('');

  const thumbnailUrl = useString('');
  const {
    handleGetWidthAndHeightThumbnail,
    handleGetWidthAndHeightVideo,
    thumbnailHeight,
    thumbnailWidth,
    videoHeight,
    videoWidth,
  } = useWidthAndHeightFile();

  const isWarmupOrCooldownOrCheerup =
    category === ExerciseContentCategory.WARMUP ||
    category === ExerciseContentCategory.COOLDOWN ||
    category === ExerciseContentCategory.MIDPOINTCHEERUP ||
    category === ExerciseContentCategory.ENDINGCHEERUP;

  useEffect(() => {
    getConfigs();
    getLevels();
  }, []);

  useEffect(() => {
    if (isExcercisesAdd) {
      return;
    }

    if (id) {
      getExerciseDetail();
    }
  }, [id]);

  useEffect(() => {
    if (!category && !id) {
      return;
    }

    if (
      (exerciseDetail?.category.key === ExerciseContentCategory.CARDIO ||
        exerciseDetail?.category.key === ExerciseContentCategory.STRENGTHENING) &&
      (category === ExerciseContentCategory.CARDIO ||
        category === ExerciseContentCategory.STRENGTHENING)
    ) {
      setFieldValue('level', exerciseDetail?.level);
      setFieldValue('upperBody', exerciseDetail?.upperBody);
      setFieldValue('lowerBody', exerciseDetail?.lowerBody);
      setFieldValue('reps', exerciseDetail?.reps);
      setFieldValue('sets', exerciseDetail?.sets);
      setFieldValue('duration', exerciseDetail?.duration);
      setFieldValue('restTime', exerciseDetail?.restTime);
    }
  }, [category]);

  const getExerciseDetail = async () => {
    const result: Exercises = await apiCall({
      url: `/exercises/${id}`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.id) {
      setValues(result as any);
      setFieldValue('category', result.category.id);
      setExerciseDetail({
        ...result,
        name: result.name,
        reps: result.reps,
        sets: result.sets,
        category: result.category,
        description: result.description,
        duration: result.duration,
      });
      videoUrl.setValue(result?.video ?? '');
      thumbnailUrl.setValue(result?.thumbnail ?? '');
      setCategory(result.category.key);
    }
  };

  const categories = useMemo(() => {
    if (isEmpty(configs)) {
      return [];
    }

    return configs.filter((el) => el.type === ConfigType.EXERCISE_CONTENT_CATEGORY);
  }, [configs]);

  const getConfigs = async () => {
    const result: Config[] = await apiCall({
      url: '/configs',
      method: 'GET',
    });

    if (result) {
      setConfigs(result ?? []);
    }
  };

  const formik = useFormik({
    initialValues: defaultExcercises,
    validationSchema: exercisesSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      showPageLoading();
      const resultUpload: {
        urlVideo: string;
        urlThumbnail: string;
        nameFileImage?: string;
        nameFileVideo?: string;
      } | null = await handleUploadVideoAndThumbnail();

      if (!resultUpload) {
        return;
      }

      const bodyRequest = {
        ...trimObject({
          name: values.name,
          reps: values.reps,
          sets: values.sets,
          duration: values.duration,
          category: values.category,
          description: values.description,
          institutionId: userProfile?.institution?.id,
          restTime: values.restTime,
          video: resultUpload.nameFileVideo,
          thumbnail: resultUpload.nameFileImage,
          level: values.level,
          upperBody: values.upperBody,
          lowerBody: values.lowerBody,
        }),
      };

      if (resultUpload.urlVideo && videoHeight.value && videoWidth.value) {
        bodyRequest.videoHeight = videoHeight.value;
        bodyRequest.videoWidth = videoWidth.value;
      }

      if (resultUpload.urlThumbnail && thumbnailHeight.value && thumbnailWidth.value) {
        bodyRequest.thumbnailHeight = thumbnailHeight.value;
        bodyRequest.thumbnailWidth = thumbnailWidth.value;
      }

      if (!resultUpload.nameFileVideo) {
        delete bodyRequest.video;
      }

      if (!resultUpload.nameFileImage) {
        delete bodyRequest.thumbnail;
      }

      if (isExcercisesAdd && isInstitutionDetail) {
        bodyRequest.institutionId = institutionId;
      }

      if (isWarmupOrCooldownOrCheerup) {
        bodyRequest.level = 1;
        bodyRequest.upperBody = 50;
        bodyRequest.lowerBody = 50;
        bodyRequest.reps = 1;
        bodyRequest.sets = 1;
        bodyRequest.duration = 1;
        bodyRequest.restTime = 1;
      }

      const result: Exercises = await apiCall({
        url: isExcercisesAdd ? `/exercises` : `/exercises/${id}`,
        method: isExcercisesAdd ? 'POST' : 'PATCH',
        data: bodyRequest,
        showError: true,
      });

      if (!result?.id) {
        closeLoading();

        return;
      }

      if (isExcercisesAdd) {
        closeLoading();
        showToastMessage('success', SUCCESS_MESSAGE.ADD_EXERCISE);
        formik.setValues(defaultExcercises);
        navigationTo(-1);

        return;
      }

      closeLoading();
      getExerciseDetail();
      showToastMessage('success', SUCCESS_MESSAGE.UPDATE_EXERCISE);
    },
  });

  const onChangeCategory = (newItem: Config) => () => {
    if (newItem) {
      setFieldValue('category', newItem.id);
      setCategory(newItem.key);
    }

    if (
      isWarmupOrCooldownOrCheerup &&
      (newItem.key === ExerciseContentCategory.STRENGTHENING ||
        newItem.key === ExerciseContentCategory.CARDIO)
    ) {
      setFieldValue('level', 0);
      setFieldValue('upperBody', undefined);
      setFieldValue('lowerBody', undefined);
      setFieldValue('reps', undefined);
      setFieldValue('sets', undefined);
      setFieldValue('duration', undefined);
      setFieldValue('restTime', undefined);
    }
  };

  const onChangeLevel = (level: number) => () => {
    setFieldValue('level', level);
  };

  const {
    values,
    errors,
    isSubmitting,
    isValid,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
  } = formik;

  const hasEmptyData = isWarmupOrCooldownOrCheerup
    ? hasEmptyValues({
        name: values.name,
        category: values.category,
      })
    : hasEmptyValues({
        name: values.name,
        reps: values.reps,
        sets: values.sets,
        duration: values.duration,
        category: values.category,
        restTime: values.restTime,
        level: values.level,
        upperBody: values.upperBody,
        lowerBody: values.lowerBody,
      });

  const disabled =
    hasEmptyData ||
    // !thumbnailUrl.value ||
    !videoUrl.value ||
    isSubmitting ||
    !isValid;

  const onCancel = () => {
    if (!isExcercisesAdd && id) {
      setValues(exerciseDetail as any);

      setFieldValue('category', exerciseDetail?.category.id);

      setCategory(exerciseDetail?.category.key);
      thumbnailUrl.setValue(exerciseDetail?.thumbnail as string);

      return;
    }

    formik.setValues(defaultExcercises);
    navigationTo(-1);
  };

  const handleDeleteExercise = async () => {
    await apiCall({
      url: `/exercises/${id}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });
    closeModal(MODAL_KEY.DELETE_EXERCISE);
    navigationTo(-1);
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_EXERCISE);
  };

  const disabledButton = useMemo(() => {
    return (
      isEqual(
        { ...exerciseDetail, category: exerciseDetail?.category.id },
        {
          ...values,
          thumbnail: thumbnailUrl.value,
          video: videoUrl.value,
        },
      ) ||
      hasEmptyData ||
      // !thumbnailUrl.value ||
      !videoUrl.value
    );
  }, [values, exerciseDetail, thumbnailUrl.value, videoUrl.value]);

  const hideDeletecAction = checkRole([RoleKey.TRAINER_INSTITUTION, RoleKey.TRAINER_BIOLOGIC]);

  const goBack = () => {
    navigationTo(-1);
  };

  const onChangeVideo = (files: File[]) => {
    const file = files[0];

    if (file && file.type.includes('video')) {
      setVideo(file);
      videoUrl.setValue(URL.createObjectURL(file));
    } else {
      showToastMessage('error', 'Error format video');
    }
  };

  const onChangeThumbnail = (files: File[]) => {
    const file = files[0];

    if (file && file.type.includes('image')) {
      setThumbnail(file);
      thumbnailUrl.setValue(URL.createObjectURL(file));
    } else {
      showToastMessage('error', 'Error format image');
    }
  };

  const handleRemoveVideo = () => {
    setVideo(null);
    videoUrl.setValue('');
  };

  const handleRemoveThumbnail = () => {
    setThumbnail(null);

    if (exerciseDetail?.thumbnail?.includes('thumbnail-default')) {
      thumbnailUrl.setValue(exerciseDetail?.thumbnail);
    } else {
      thumbnailUrl.setValue('');
    }
  };

  const handleUploadVideoAndThumbnail = async () => {
    let urlVideo = videoUrl.value;
    let urlThumbnail = thumbnailUrl.value;

    let nameFileVideo = '';

    let nameFileImage = '';

    try {
      if (video) {
        const ext = getExtFileName(video.name);

        nameFileVideo = `video-exercise-${randomString(4)}-${Date.now()}${ext}`;

        urlVideo = await UploadFile(video, nameFileVideo);
      }

      if (thumbnail) {
        const thumbnailExt = getExtFileName(thumbnail.name);

        nameFileImage = `thumbnail-exercise-${randomString(4)}-${Date.now()}${thumbnailExt}`;

        urlThumbnail = await UploadFile(thumbnail, nameFileImage);
      }

      if (!thumbnail && !thumbnailUrl.value) {
        const defaultThumbnailFile = base64ToFile(
          defaultThumbnailBase64,
          'default-thumbnail.png',
          'image/png',
        );

        const defaultThumbnailExt = getExtFileName(defaultThumbnailFile.name);

        nameFileImage = `thumbnail-default${defaultThumbnailExt}`;

        urlThumbnail = await UploadFile(defaultThumbnailFile, nameFileImage);
      }

      return {
        urlVideo,
        urlThumbnail,
        nameFileImage,
        nameFileVideo,
      };
    } catch (error) {
      console.log(error);

      return null;
    }
  };

  const getLevels = () => {
    const levelsDefault = Object.values(levelsExercise).filter(
      (value) => typeof value === 'number',
    ) as number[];

    setLevels(levelsDefault);
  };

  const handleChangeUpperBody = (event: any) => {
    const value = event.target.value;

    if (value > 100) {
      setFieldValue('upperBody', 100);
    } else if (value < 0) {
      setFieldValue('upperBody', 0);
    } else {
      setFieldValue('upperBody', value);
    }
  };

  const handleChangeLowerBody = (event: any) => {
    const value = event.target.value;

    if (value > 100) {
      setFieldValue('lowerBody', 100);
    } else if (value < 0) {
      setFieldValue('upperBody', 0);
    } else {
      setFieldValue('lowerBody', value);
    }
  };

  const handleKeyDownLowerAndUpperBody = (e: any) => {
    if (e.key === '-') {
      e.preventDefault();
    }
  };

  return {
    values,
    errors,
    disabled,
    handleChange,
    handleSubmit,
    onChangeCategory,
    categories,
    onCancel,
    exerciseId: isExcercisesAdd ? '' : id,
    handleDeleteExercise,
    disabledButton,
    hideDeletecAction,
    exerciseDetail,
    goBack,
    onChangeVideo,
    onChangeThumbnail,
    videoUrl,
    thumbnailUrl,
    handleRemoveVideo,
    handleRemoveThumbnail,
    handleGetWidthAndHeightVideo,
    handleGetWidthAndHeightThumbnail,
    levels,
    onChangeLevel,
    handleChangeUpperBody,
    handleChangeLowerBody,
    handleKeyDownLowerAndUpperBody,
    categoryKey: category,
  };
};
